import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCartContext } from '../../contexts/CartContext'
import { Heart, Trash2, MapPin, Package } from 'lucide-react'
import { formatPrice } from '../../utils/currency'
import PageLayout from '../layout/PageLayout'

export default function Cart() {
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null

  const {
    cart,
    loading,
    error,
    removeItem,
    getTotalItems
  } = useCartContext()

  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set())

  // Handle remove item
  const handleRemoveItem = async (itemId: string) => {
    try {
      setUpdatingItems(prev => new Set(prev).add(itemId))
      await removeItem(itemId)
      if (tg?.showAlert) {
        tg.showAlert('Item removed from favorites')
      }
    } catch (error) {
      console.error('Failed to remove item:', error)
      if (tg?.showAlert) {
        tg.showAlert('Failed to remove item. Please try again.')
      }
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }
  }

  // Group cart items by seller
  const groupItemsBySeller = () => {
    if (!cart || !cart.items) return {}

    return cart.items.reduce((acc: any, item) => {
      const sellerId = item.product?.owner?.telegram_id || 'unknown'
      if (!acc[sellerId]) {
        acc[sellerId] = {
          seller: item.product?.owner,
          items: []
        }
      }
      acc[sellerId].items.push(item)
      return acc
    }, {})
  }

  useEffect(() => {
    console.log('Cart component mounted - tg:', !!tg, 'cart:', !!cart, 'loading:', loading, 'error:', error)
    if (cart) {
      console.log('Cart data structure:', JSON.stringify(cart, null, 2))
      console.log('Cart items type:', typeof cart.items, 'isArray:', Array.isArray(cart.items))
      if (!cart.items) {
        console.warn('Cart items is null or undefined!')
      }
    }

    if (!tg) return

    // Show back button
    try {
      const handleBack = () => window.history.back()
      tg.BackButton?.show()
      tg.BackButton?.onClick(handleBack)

      // Hide main button since we're not using checkout
      tg.MainButton?.hide()

      return () => {
        tg.BackButton?.offClick(handleBack)
        tg.MainButton?.hide()
      }
    } catch (error) {
      console.warn('Failed to setup back button:', error)
    }
  }, [tg, cart])

  return (
    <PageLayout>
      <div className="max-w-md mx-auto pt-2">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading cart...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="text-red-600 text-sm flex items-center gap-2">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Empty Favorites */}
          {!loading && cart && (!cart.items || cart.items.length === 0) && (
            <div className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.05)] p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-red-50 to-rose-100
                flex items-center justify-center">
                <Heart className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-lg font-bold text-gray-800 mb-1">
                No Favorites Yet
              </h2>
              <p className="text-gray-400 text-sm mb-6">
                Tap the heart on products you love
              </p>
              <button
                onClick={() => navigate('/products')}
                className="bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold text-sm
                  hover:bg-blue-600 active:scale-[0.98] transition-all shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
              >
                Discover Products
              </button>
            </div>
          )}

          {/* Cart Items Grouped by Seller */}
          {cart && cart.items && Array.isArray(cart.items) && cart.items.length > 0 && (
            <>
              <div className="space-y-6 mb-6">
                {Object.entries(groupItemsBySeller()).map(([sellerId, group]: [string, any]) => (
                  <div key={sellerId} className="space-y-3">
                    {/* Seller Header with Contact Options */}
                    <div className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-gray-800">
                            {group.seller?.first_name || group.seller?.username || 'Seller'}
                          </p>
                          <p className="text-sm text-gray-400">
                            {group.items.length} item{group.items.length > 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {group.seller?.username && (
                            <a
                              href={`https://t.me/${group.seller.username}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3.5 py-2 bg-green-500 text-white rounded-xl text-xs font-semibold
                                hover:bg-green-600 active:scale-95 transition-all text-center"
                            >
                              Contact
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Items from this seller */}
                    {group.items.map((item: any) => {
                      if (!item || !item.id) {
                        console.error('Invalid cart item:', item)
                        return null
                      }

                      const isUpdating = updatingItems.has(item.id)

                      return (
                    <div
                      key={item.id}
                      onClick={() => item.product_id && navigate(`/product/${item.product_id}`)}
                      className={`bg-white rounded-2xl p-3.5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                        transition-opacity cursor-pointer active:scale-[0.99] ${
                        isUpdating ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Product Image */}
                        {item.product?.image_url ? (
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                        )}

                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-800 text-sm">
                            {item.product?.name || 'Product'}
                          </h3>
                          {item.product?.city_name && (
                            <div className="flex items-center gap-1 text-gray-500 text-xs mt-1">
                              <MapPin className="w-3 h-3" />
                              <span>{item.product.city_name}</span>
                            </div>
                          )}
                          <p className="font-semibold text-blue-600 mt-1">
                            {formatPrice(item.price, item.product?.currency)}
                          </p>
                        </div>

                        {/* Remove from favorites */}
                        <div className="flex flex-col items-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id) }}
                            disabled={isUpdating}
                            className="text-red-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                      )
                    })}
                  </div>
                ))}
              </div>

              {/* Cart Summary */}
              <div className="bg-white rounded-2xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)] mb-4">
                <h3 className="font-semibold text-gray-800 mb-3">Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Items:</span>
                    <span className="font-medium">{getTotalItems()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sellers:</span>
                    <span className="font-medium">{Object.keys(groupItemsBySeller()).length}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-700 font-medium">Estimated Total:</span>
                    <span className="font-semibold text-blue-600">{formatPrice(cart.total)}</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-xs text-blue-700 text-center">
                    Contact sellers directly to arrange purchase and payment
                  </p>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="space-y-3 mb-2">
                <button
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl
                    transition-all active:scale-[0.98] text-sm shadow-[0_4px_12px_rgba(59,130,246,0.3)]"
                  onClick={() => navigate('/products')}
                >
                  Continue Shopping
                </button>
                <button
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl
                    transition-all active:scale-[0.98] text-sm"
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </button>
              </div>
            </>
          )}
      </div>
    </PageLayout>
  )
}